import React, { useEffect, useMemo, useState } from "react";
import { collection, addDoc, doc, getDoc, updateDoc } from "firebase/firestore";
import { EmailOutlined, PhoneOutlined } from "@mui/icons-material";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

const STATUS_OPTIONS = [
  { value: "Pending", label: "Pending" },
  { value: "Due", label: "Due" },
  { value: "Paid", label: "Paid" },
  { value: "Completed", label: "Completed" },
  { value: "Canceled", label: "Canceled" },
];

const STATUS_META = {
  pending: {
    label: "Pending",
    pill: "op-pill--status-pending",
    tag: "od-tag--pending",
  },
  due: { label: "Due", pill: "op-pill--status-due", tag: "od-tag--downloaded" },
  paid: {
    label: "Paid",
    // reuse the in-progress styling for Paid unless/until a paid style exists
    pill: "op-pill--status-inprogress",
    tag: "od-tag--inprogress",
  },
  completed: {
    label: "Completed",
    pill: "op-pill--status-completed",
    tag: "od-tag--completed",
  },
  canceled: {
    label: "Canceled",
    // keep existing cancelled pill class for styling
    pill: "op-pill--status-cancelled",
    tag: "od-tag--cancelled",
  },
};

const normalizeStatus = (value) => {
  if (!value) return "";
  return String(value)
    .toLowerCase()
    .replace(/[-_\s]+/g, "");
};

const getStatusMeta = (status) =>
  STATUS_META[normalizeStatus(status)] || STATUS_META.pending;

const formatCurrency = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return "—";
  return num.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
};

const formatDateTime = (value) => {
  if (!value) return "—";
  let date = null;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value?.toDate === "function") {
    try {
      date = value.toDate();
    } catch (_) {
      date = null;
    }
  } else if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) date = parsed;
  }
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
};

const formatRequestId = (id) => {
  if (!id) return "Request";
  const str = String(id);
  if (str.length <= 10) return `Request #${str}`;
  return `Request #${str.slice(0, 4)}…${str.slice(-3)}`;
};

const extractUrl = (item) => {
  if (!item) return "";
  if (typeof item === "string") return item;
  if (typeof item === "object") {
    return (
      item.url ||
      item.downloadURL ||
      item.downloadUrl ||
      item.file ||
      item.src ||
      ""
    );
  }
  return "";
};

const extractName = (item, fallback) => {
  if (!item) return fallback;
  if (typeof item === "string") {
    const parts = item.split("/");
    return parts[parts.length - 1] || fallback;
  }
  if (typeof item === "object") {
    return (
      item.name || item.fileName || item.filename || item.title || fallback
    );
  }
  return fallback;
};

const toAssetList = (sources = [], kind) => {
  if (!Array.isArray(sources)) return [];
  return sources
    .map((entry, index) => {
      const url = extractUrl(entry);
      if (!url) return null;
      const name = extractName(entry, `${kind} asset ${index + 1}`);
      const quantity = Number(entry?.quantity ?? entry?.count ?? 1);
      const sizeLabel =
        entry?.widthIn && entry?.heightIn
          ? `${entry.widthIn}" × ${entry.heightIn}"`
          : entry?.dimensions || entry?.size || "";

      return {
        id: `${kind}-${index}`,
        name,
        url,
        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
        sizeLabel,
        kind,
      };
    })
    .filter(Boolean);
};

const buildCustomerName = (order, customerData) => {
  if (order?.customerName) return order.customerName;
  if (!customerData) return order?.customerId || "Customer";
  if (customerData.freshbooksClientId?.organization) {
    return customerData.freshbooksClientId.organization;
  }
  const first = customerData.firstName || customerData.first_name || "";
  const last = customerData.lastName || customerData.last_name || "";
  const combined = `${first} ${last}`.trim();
  return combined || order?.customerId || "Customer";
};

function ImageOrderDetails({
  selectedOrder,
  setSelectedOrder,
  handleCloseDetails,
  handleStatusChange,
  db,
  customerData,
  refreshOrders,
}) {
  const [previewUrl, setPreviewUrl] = useState("");
  const [localTotal, setLocalTotal] = useState(selectedOrder?.total ?? null);
  const [isSettingQuote, setIsSettingQuote] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [pendingCompletedFiles, setPendingCompletedFiles] = useState([]);
  const [isUploadingCompleted, setIsUploadingCompleted] = useState(false);

  useEffect(() => {
    setLocalTotal(selectedOrder?.total ?? selectedOrder?.price ?? null);
  }, [selectedOrder?.id, selectedOrder?.total, selectedOrder?.price]);

  useEffect(() => {
    setPendingCompletedFiles([]);
  }, [selectedOrder?.id]);

  const submittedAssets = useMemo(() => {
    if (!selectedOrder) return [];
    const lists = [selectedOrder.images, selectedOrder.attachments];
    return lists.flatMap((list, idx) =>
      toAssetList(list, idx === 0 ? "submitted" : "attachment")
    );
  }, [selectedOrder]);

  const completedAssets = useMemo(() => {
    if (!selectedOrder) return [];
    const lists = [selectedOrder.completedAttachments, selectedOrder.response];
    return lists.flatMap((list) => toAssetList(list, "completed"));
  }, [selectedOrder]);

  if (!selectedOrder) return null;

  const statusMeta = getStatusMeta(selectedOrder.status);

  const hasAssets = submittedAssets.length > 0 || completedAssets.length > 0;
  const statusKey = normalizeStatus(selectedOrder.status);
  const displayStatus =
    statusKey === "paid" && hasAssets
      ? {
          label: "Ready",
          pill: "op-pill--status-downloaded",
          tag: "od-tag--downloaded",
        }
      : statusMeta;
  const customerName = buildCustomerName(selectedOrder, customerData);
  const createdAtLabel = formatDateTime(selectedOrder?.createdAt);

  const handleSetQuote = async () => {
    const existing = localTotal ? Number(localTotal).toFixed(2) : "";
    const raw = window.prompt(
      "Enter the quote amount for this request (USD):",
      existing
    );
    if (raw === null) return; // cancelled
    const parsed = Number.parseFloat(raw);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      alert("Please enter a valid positive amount.");
      return;
    }

    try {
      setIsSettingQuote(true);
      const orderRef = doc(db, "artOrders", selectedOrder.id);
      await updateDoc(orderRef, { total: parsed, status: "Due" });
      const snapshot = await getDoc(orderRef);
      if (snapshot.exists()) {
        const data = snapshot.data();
        setSelectedOrder((prev) => (prev ? { ...prev, ...data } : prev));
        setLocalTotal(data.total ?? parsed);
      } else {
        setSelectedOrder((prev) =>
          prev ? { ...prev, total: parsed, status: "Due" } : prev
        );
        setLocalTotal(parsed);
      }

      await handleStatusChange(selectedOrder.id, "Due");
      if (typeof refreshOrders === "function") {
        refreshOrders().catch((err) =>
          console.warn("Failed to refresh orders after quote update", err)
        );
      }

      if (customerData?.email) {
        await addDoc(collection(db, "mail"), {
          to: customerData.email,
          message: {
            subject: "Your design request quote is ready",
            html: `
              <div style="font-family: 'Anek Latin', sans-serif; background-color: #f5f7fa; padding: 40px;">
                <div style="max-width: 600px; margin: auto; background-color: white; border-radius: 10px; overflow: hidden; box-shadow: 0 5px 15px rgba(0,0,0,0.1);">
                  <div style="background-color: #1e1e2f; padding: 20px; text-align: center;">
                    <h1 style="color: #ffffff; margin: 0;">Quote available</h1>
                    <p style="color: #cfcfcf; margin: 5px 0 0;">Next steps for your request</p>
                  </div>
                  <div style="padding: 30px; color: #334155;">
                    <p style="margin: 0 0 12px;">Hi ${customerName},</p>
                    <p style="margin: 0 0 12px;">We just added a quote to your image request. Sign in to approve or reply with any questions.</p>
                    <p style="margin: 0;">Need help? Email <a href="mailto:cs@seeourdesigns.com">cs@seeourdesigns.com</a>.</p>
                  </div>
                  <div style="background-color: #f0f0f0; text-align: center; padding: 15px; font-size: 12px; color: #999;">
                    &copy; ${new Date().getFullYear()} See Our Designs. All rights reserved.
                  </div>
                </div>
              </div>
            `,
          },
        });
      }
    } catch (error) {
      console.error("Error updating quote:", error);
      alert("Failed to update the quote. Check the console for details.");
    } finally {
      setIsSettingQuote(false);
    }
  };

  const sendCompletionEmail = async () => {
    const email = customerData?.email;
    if (!email) return;
    try {
      const customerNameSafe = customerName || "there";
      await addDoc(collection(db, "mail"), {
        to: email,
        message: {
          subject: "Your design request is complete",
          html: `
            <div style="font-family: 'Anek Latin', sans-serif; background-color: #f5f7fa; padding: 40px;">
              <div style="max-width: 600px; margin: auto; background-color: white; border-radius: 10px; overflow: hidden; box-shadow: 0 5px 15px rgba(0,0,0,0.1);">
                <div style="background-color: #1e1e2f; padding: 20px; text-align: center;">
                  <h1 style="color: #ffffff; margin: 0;">Order completed</h1>
                  <p style="color: #cfcfcf; margin: 5px 0 0;">Your files are ready</p>
                </div>
                <div style="padding: 30px; color: #334155;">
                  <p style="margin: 0 0 12px;">Hi ${customerNameSafe},</p>
                  <p style="margin: 0 0 12px;">We just wrapped your design request ${formatRequestId(selectedOrder.id)}. Download your finished artwork from your See Our Designs account or reply if anything needs to be adjusted.</p>
                  <p style="margin: 0;">Need help? Email <a href="mailto:cs@seeourdesigns.com">cs@seeourdesigns.com</a>.</p>
                </div>
                <div style="background-color: #f0f0f0; text-align: center; padding: 15px; font-size: 12px; color: #999;">
                  &copy; ${new Date().getFullYear()} See Our Designs. All rights reserved.
                </div>
              </div>
            </div>
          `,
        },
      });
    } catch (err) {
      console.error("Failed to enqueue completion email:", err);
    }
  };

  const updateStatus = async (nextStatus) => {
    await handleStatusChange(selectedOrder.id, nextStatus);
    if (nextStatus === "Completed") {
      await sendCompletionEmail();
    }
  };

  const handleCompletedUpload = async () => {
    if (!selectedOrder) return;
    if (!pendingCompletedFiles.length) {
      window.alert("Select at least one file to upload.");
      return;
    }

    try {
      setIsUploadingCompleted(true);
      const storage = getStorage();
      const uploaded = [];

      for (const file of pendingCompletedFiles) {
        const safeName = file.name.replace(/\s+/g, "-");
        const storagePath = `artOrders/${selectedOrder.id}/completed/${Date.now()}-${safeName}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        uploaded.push({ name: file.name, url });
      }

      const existingResponses = Array.isArray(selectedOrder.response)
        ? [...selectedOrder.response]
        : [];
      const combinedResponses = [...existingResponses, ...uploaded];

      const orderRef = doc(db, "artOrders", selectedOrder.id);
      await updateDoc(orderRef, {
        response: combinedResponses,
        status: "Completed",
      });

      setSelectedOrder((prev) =>
        prev
          ? { ...prev, response: combinedResponses, status: "Completed" }
          : prev
      );

      await updateStatus("Completed");

      if (typeof refreshOrders === "function") {
        refreshOrders().catch((err) =>
          console.warn("Failed to refresh after upload", err)
        );
      }

      setPendingCompletedFiles([]);
      window.alert("Completed files uploaded successfully.");
    } catch (error) {
      console.error("Failed to upload completed files:", error);
      window.alert("Failed to upload files. Check console for details.");
    } finally {
      setIsUploadingCompleted(false);
    }
  };

  return (
    <div className="od">
      <div className="od-head">
        <div className="od-title">
          <button
            type="button"
            className="op-btn op-btn--ghost od-back"
            onClick={() => {
              handleCloseDetails?.();
              setSelectedOrder(null);
            }}
          >
            ← Back
          </button>
          <h2>{formatRequestId(selectedOrder.id)}</h2>
          <div className="od-sub">
            {customerName} • Submitted {createdAtLabel}
          </div>
          <div className="od-badges">
            <span className={`op-pill ${displayStatus.pill}`}>
              {displayStatus.label}
            </span>
            <span
              className={`od-tag ${hasAssets ? "od-tag--downloaded" : "od-tag--pay-pending"}`}
            >
              {hasAssets ? "Assets ready" : "Awaiting assets"}
            </span>
            {localTotal ? (
              <span className="od-tag od-tag--pay-success">
                Quote {formatCurrency(localTotal)}
              </span>
            ) : (
              <span className="od-tag od-tag--pay-unpaid">Quote pending</span>
            )}
          </div>
        </div>

        <div className="od-head-actions">
          <div className="od-menu">
            <button
              type="button"
              className="op-btn op-btn--ghost"
              onClick={() => {
                try {
                  refreshOrders && refreshOrders();
                } catch (_) {}
              }}
            >
              Refresh
            </button>
          </div>
          <div className="od-menu">
            <button
              type="button"
              className="op-btn"
              onClick={handleSetQuote}
              disabled={isSettingQuote}
            >
              {isSettingQuote
                ? "Saving…"
                : localTotal
                  ? "Update quote"
                  : "Set quote"}
            </button>
          </div>
          <div className="od-menu">
            <button className="op-btn" onClick={() => setStatusOpen((v) => !v)}>
              Set Status ▾
            </button>
            {statusOpen && (
              <div className="od-menu__list">
                <button
                  className="od-menu__item"
                  onClick={async () => {
                    await updateStatus("Pending");
                    setStatusOpen(false);
                  }}
                >
                  Pending
                </button>
                <button
                  className="od-menu__item"
                  onClick={async () => {
                    await updateStatus("Due");
                    setStatusOpen(false);
                  }}
                >
                  Due
                </button>
                <button
                  className="od-menu__item"
                  onClick={async () => {
                    await updateStatus("Paid");
                    setStatusOpen(false);
                  }}
                >
                  Paid
                </button>
                <button
                  className="od-menu__item"
                  onClick={async () => {
                    await updateStatus("Completed");
                    setStatusOpen(false);
                  }}
                >
                  Completed
                </button>
                <button
                  className="od-menu__item"
                  onClick={async () => {
                    await updateStatus("Canceled");
                    setStatusOpen(false);
                  }}
                >
                  Canceled
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="od-grid">
        <div>
          <section className="od-card">
            <div className="od-card__header">
              <div className="od-card__title">Request summary</div>
            </div>
            <div className="od-rows">
              <div className="od-row">
                <span>Customer</span>
                <strong>{customerName}</strong>
              </div>
              <div className="od-row">
                <span>Submitted</span>
                <span>{createdAtLabel}</span>
              </div>
              <div className="od-row">
                <span>Status</span>
                <span>{displayStatus.label}</span>
              </div>
              <div className="od-row od-row--total">
                <span>Quote</span>
                <strong>{formatCurrency(localTotal)}</strong>
              </div>
            </div>
            {selectedOrder?.notes && (
              <div className="od-muted" style={{ marginTop: 12 }}>
                {selectedOrder.notes}
              </div>
            )}
            {selectedOrder?.instructions && (
              <div className="od-muted" style={{ marginTop: 12 }}>
                <strong>Instructions:</strong> {selectedOrder.instructions}
              </div>
            )}
          </section>

          <section className="od-card">
            <div className="od-card__header">
              <div className="od-card__title">Submitted assets</div>
            </div>
            {submittedAssets.length === 0 ? (
              <div className="od-muted">No assets uploaded yet.</div>
            ) : (
              <div className="od-thumbs">
                {submittedAssets.map((asset) => (
                  <div key={asset.id} className="od-thumb">
                    <img src={asset.url} alt={asset.name} />
                    <div className="od-thumb__meta">
                      <div className="od-thumb__title">{asset.name}</div>
                      <div className="od-thumb__sub">
                        Quantity: {asset.quantity}
                      </div>
                      {asset.sizeLabel && (
                        <div className="od-thumb__sub">{asset.sizeLabel}</div>
                      )}
                      <div className="od-thumb__foot">Submitted asset</div>
                    </div>
                    <div className="od-thumb__actions">
                      <button
                        type="button"
                        className="op-btn"
                        onClick={() => setPreviewUrl(asset.url)}
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        className="op-btn"
                        onClick={() =>
                          window.open(asset.url, "_blank", "noopener")
                        }
                      >
                        Open
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="od-card">
            <div className="od-card__header">
              <div className="od-card__title">Completed assets</div>
            </div>
            {completedAssets.length === 0 ? (
              <div className="od-muted">Nothing delivered yet.</div>
            ) : (
              <div className="od-thumbs">
                {completedAssets.map((asset) => (
                  <div key={asset.id} className="od-thumb">
                    <img src={asset.url} alt={asset.name} />
                    <div className="od-thumb__meta">
                      <div className="od-thumb__title">{asset.name}</div>
                      {asset.sizeLabel && (
                        <div className="od-thumb__sub">{asset.sizeLabel}</div>
                      )}
                      <div className="od-thumb__foot">Delivered asset</div>
                    </div>
                    <div className="od-thumb__actions">
                      <button
                        type="button"
                        className="op-btn"
                        onClick={() => setPreviewUrl(asset.url)}
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        className="op-btn"
                        onClick={() =>
                          window.open(asset.url, "_blank", "noopener")
                        }
                      >
                        Open
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="od-rows" style={{ marginTop: 12, gap: 10 }}>
              <label className="op-btn" style={{ width: "fit-content" }}>
                Upload completed files
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(event) => {
                    const files = Array.from(event.target.files || []);
                    if (!files.length) return;
                    setPendingCompletedFiles((prev) => [...prev, ...files]);
                    event.target.value = "";
                  }}
                />
              </label>

              {pendingCompletedFiles.length > 0 && (
                <div className="od-thumbs">
                  {pendingCompletedFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="od-thumb"
                      style={{ opacity: 0.85 }}
                    >
                      <img src={URL.createObjectURL(file)} alt={file.name} />
                      <div className="od-thumb__meta">
                        <div className="od-thumb__title">{file.name}</div>
                        <div className="od-thumb__sub">Pending upload</div>
                      </div>
                      <div className="od-thumb__actions">
                        <button
                          type="button"
                          className="op-btn"
                          onClick={() => {
                            setPendingCompletedFiles((prev) =>
                              prev.filter((_, i) => i !== index)
                            );
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {pendingCompletedFiles.length > 0 && (
                <div>
                  <button
                    type="button"
                    className="op-btn op-btn--primary"
                    onClick={handleCompletedUpload}
                    disabled={isUploadingCompleted}
                  >
                    {isUploadingCompleted
                      ? "Uploading…"
                      : "Send completed files"}
                  </button>
                </div>
              )}
            </div>
          </section>
        </div>

        <aside>
          <section className="od-card">
            <div className="od-card__header">
              <div className="od-card__title">Customer</div>
            </div>
            <div className="od-list">
              <div className="od-list__item">
                <div className="od-avatar">
                  {customerName.slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div className="od-list__title">{customerName}</div>
                  <div className="od-list__sub">
                    {selectedOrder?.customerId || "Art client"}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="od-card">
            <div className="od-card__header">
              <div className="od-card__title">Contact information</div>
            </div>
            <div className="od-rows">
              <div className="od-row">
                <EmailOutlined fontSize="small" />
                {customerData?.email ? (
                  <a href={`mailto:${customerData.email}`}>
                    {customerData.email}
                  </a>
                ) : (
                  <span>No email on file</span>
                )}
              </div>
              <div className="od-row">
                <PhoneOutlined fontSize="small" />
                <span>{customerData?.phone || "No phone number"}</span>
              </div>
            </div>
          </section>

          {selectedOrder?.internalNotes &&
            Array.isArray(selectedOrder.internalNotes) && (
              <section className="od-card">
                <div className="od-card__header">
                  <div className="od-card__title">Internal notes</div>
                </div>
                {selectedOrder.internalNotes.length ? (
                  <ul className="od-note-list">
                    {selectedOrder.internalNotes.map((note, index) => (
                      <li key={index} className="od-note">
                        <div className="od-note__text">{note.text || note}</div>
                        {note?.createdAt && (
                          <div className="od-note__meta">
                            {formatDateTime(note.createdAt)}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="od-muted">No notes yet.</div>
                )}
              </section>
            )}
        </aside>
      </div>

      {previewUrl && (
        <div
          role="presentation"
          className="op-modal"
          onClick={() => setPreviewUrl("")}
          style={{
            position: "fixed",
            inset: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 5000,
            padding: 16,
          }}
        >
          <div
            className="op-modal__content"
            role="presentation"
            onClick={(event) => event.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 18px 60px rgba(0,0,0,0.45)",
              padding: 12,
              maxWidth: "90vw",
              maxHeight: "90vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <button
              type="button"
              onClick={() => setPreviewUrl("")}
              aria-label="Close preview"
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                border: "none",
                background: "rgba(0,0,0,0.7)",
                color: "#fff",
                borderRadius: 8,
                padding: "6px 10px",
                cursor: "pointer",
              }}
            >
              Close ✕
            </button>
            <img
              src={previewUrl}
              alt="Preview"
              style={{
                display: "block",
                maxWidth: "85vw",
                maxHeight: "80vh",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                backgroundColor: "#ababab",
                border: "4px solid #fff",
                borderRadius: 8,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ImageOrderDetails;
